/* Pure helpers behind the Exercise names sheet: one row per logged name, a
 * merge preview and an in-memory undo for a merge. The rename itself is
 * logic/names renameExerciseEverywhere (shared with the MCP server); this
 * file only describes it for the sheet. */

import { isAssisted, sessionBest } from '../logic/records';
import { renamePreview } from '../logic/names';
import { dayNum } from './statsData';

function isObj(v) {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

function has(obj, key) {
  return isObj(obj) && Object.prototype.hasOwnProperty.call(obj, key);
}

/* The set that best describes a session: the shared sessionBest, else (a
 * bodyweight exercise logged without load) the done set with the most reps. */
function topSet(sets, assisted) {
  const best = sessionBest(sets, { assisted });
  if (best) return { weight: best.weight, reps: best.reps };
  let reps = 0;
  (Array.isArray(sets) ? sets : []).forEach(s => {
    if (isObj(s) && s.done && Number(s.reps) > reps) reps = Number(s.reps);
  });
  return reps > 0 ? { weight: 0, reps } : null;
}

/* Every logged name with its dates (a done set) and top sets, oldest first. */
function collect(logs, exerciseMeta) {
  const out = new Map();
  Object.keys(isObj(logs) ? logs : {}).filter(d => dayNum(d) !== null).sort().forEach(date => {
    const day = logs[date];
    if (!isObj(day)) return;
    Object.keys(day).forEach(name => {
      const sets = day[name];
      if (!Array.isArray(sets) || !sets.some(s => isObj(s) && s.done)) return;
      if (!out.has(name)) out.set(name, { dates: [], tops: [], assisted: isAssisted(name, exerciseMeta) });
      const row = out.get(name);
      row.dates.push(date);
      const top = topSet(sets, row.assisted);
      if (top) row.tops.push({ date, ...top });
    });
  });
  return out;
}

function planDaysUsing(plan, name) {
  return (Array.isArray(plan) ? plan : [])
    .filter(day => isObj(day) && Array.isArray(day.exercises) && day.exercises.some(e => isObj(e) && e.name === name))
    .map(day => (typeof day.name === 'string' && day.name ? day.name : 'Unnamed day'));
}

/**
 * One row per logged exercise name (a date with a done set), A-Z.
 * @param {object} state reads logs, plan, exerciseMeta, exerciseAliases
 * @param {string} today YYYY-MM-DD
 * @param {{staleDays?:number}} [opts]
 * @returns {Array<{name, sessions:number, firstDate:string, lastDate:string,
 *   top:{date, weight, reps}|null, planDays:string[], alias:string|null, stale:boolean}>}
 *   top = the best set of the latest session with one; alias = the name this
 *   one was merged into (a straggler logged after the merge); stale = not
 *   trained in the last staleDays days
 */
export function exerciseNameRows(state, today, { staleDays = 56 } = {}) {
  const t = dayNum(today);
  const aliases = isObj(state?.exerciseAliases) ? state.exerciseAliases : {};
  const rows = [];
  collect(state?.logs, state?.exerciseMeta).forEach((info, name) => {
    const lastDate = info.dates[info.dates.length - 1];
    const alias = has(aliases, name) && typeof aliases[name] === 'string' && aliases[name] !== name ? aliases[name] : null;
    rows.push({
      name,
      sessions: info.dates.length,
      firstDate: info.dates[0],
      lastDate,
      top: info.tops.length ? info.tops[info.tops.length - 1] : null,
      planDays: planDaysUsing(state?.plan, name),
      alias,
      stale: t !== null && t - dayNum(lastDate) >= staleDays,
    });
  });
  // Plain code-unit order, not localeCompare: ICU skips punctuation, which
  // would split 'Curl (machine)' from 'Curl (machine, per arm)'.
  const key = r => r.name.toLowerCase();
  return rows.sort((a, b) => (key(a) < key(b) ? -1 : key(a) > key(b) ? 1 : a.name < b.name ? -1 : 1));
}

/**
 * The latest top sets of one name, newest first.
 * @param {object} logs state.logs
 * @param {string} name
 * @param {{limit?:number, exerciseMeta?:object}} [opts]
 * @returns {Array<{date, weight, reps}>}
 */
export function recentTopSets(logs, name, { limit = 3, exerciseMeta } = {}) {
  const info = collect(logs, exerciseMeta).get(name);
  return info ? info.tops.slice(-limit).reverse() : [];
}

/**
 * Everything the merge preview shows for folding `from` into `to`: both
 * names side by side and what moves.
 * @param {object} state
 * @param {string} from the name that goes away
 * @param {string} to the name that stays
 * @returns {{from:object, to:object, dates:string[], overlapping:string[], planDays:string[]}}
 *   from/to: {name, sessions, firstDate, lastDate, tops} (tops newest first);
 *   dates = dates whose `from` sets move; overlapping = dates that have
 *   both names (their sets are joined, `to` first)
 */
export function mergePreview(state, from, to) {
  const all = collect(state?.logs, state?.exerciseMeta);
  const side = name => {
    const info = all.get(name);
    return {
      name,
      sessions: info ? info.dates.length : 0,
      firstDate: info ? info.dates[0] : null,
      lastDate: info ? info.dates[info.dates.length - 1] : null,
      tops: info ? info.tops.slice(-3).reverse() : [],
    };
  };
  const p = renamePreview(state, from, to);
  return { from: side(from), to: side(to), dates: p.dates, overlapping: p.overlapping, planDays: p.planDays };
}

/* ── Undo ────────────────────────────────────────────────────────────────── */

/* The parts a merge rewrites and how deep to diff them: logs and
 * prescriptions per date and name, swaps per date and id, the name maps per
 * name. Arrays (plan, programs, customExercises) are restored whole. */
const UNDO_DEPTH = {
  logs: 2, swaps: 2, prescriptions: 2, exerciseMeta: 1, exerciseAliases: 1,
  plan: 0, programs: 0, customExercises: 0,
};

/**
 * What it takes to undo a change from `before` to `after`: the old value
 * (or its absence) of every path that differs, down to the depth above. The
 * merge shares untouched parts with its input, so this compares references.
 * Kept in memory only; it never reaches the synced state.
 * @param {object} before state before the merge
 * @param {object} after renameExerciseEverywhere(before, ...)
 * @returns {Array<{path:string[], had:boolean, value:*}>}
 */
export function undoPatch(before, after) {
  const out = [];
  const walk = (path, b, a, hadB, depth) => {
    if (b === a) return;
    if (depth > 0 && isObj(b) && isObj(a)) {
      new Set([...Object.keys(b), ...Object.keys(a)]).forEach(k => walk([...path, k], b[k], a[k], has(b, k), depth - 1));
      return;
    }
    out.push({ path, had: hadB, value: b });
  };
  if (!isObj(before) || !isObj(after)) return out;
  Object.keys(UNDO_DEPTH).forEach(k => walk([k], before[k], after[k], has(before, k), UNDO_DEPTH[k]));
  return out;
}

/**
 * Put back the paths of an undo patch on the current state, leaving every
 * other change (a set ticked meanwhile, a sync from Claude) alone.
 * @param {object} state current state
 * @param {Array} patch undoPatch()
 * @returns {object} new state
 */
export function applyUndo(state, patch) {
  if (!isObj(state) || !Array.isArray(patch)) return state;
  let next = state;
  patch.forEach(({ path, had, value }) => {
    const set = (obj, i) => {
      const copy = isObj(obj) ? { ...obj } : {};
      const key = path[i];
      if (i === path.length - 1) {
        if (had) copy[key] = value;
        else delete copy[key];
      } else {
        copy[key] = set(copy[key], i + 1);
      }
      return copy;
    };
    next = set(next, 0);
  });
  return next;
}
